import { useCallback } from 'react';

import {
	canUserInvite,
	getInviteesWithConfluenceAccess,
	getInviteesWithoutJiraAccess,
	getUsersFromCollaborationGraph,
} from '@atlassian/invite-prompt';
import type { Invitees } from '@atlassian/invite-prompt';

import { useSessionData } from '@confluence/session-data';

import { useFeatureExposed } from '../feature-exposure';

export const useGetInvitees = () => {
	const { cloudId: confluenceCloudId, userId } = useSessionData();
	const fireExposure = useFeatureExposed();

	return useCallback(
		async (spaceKey: string, jiraCloudId: string): Promise<Invitees> => {
			if (!userId) {
				return [];
			}

			const collaborators = await getUsersFromCollaborationGraph(confluenceCloudId, userId);

			if (!collaborators || collaborators.length === 0) {
				fireExposure('NO_COLLABORATORS_FOUND');
				return [];
			}

			const inviteesWithoutJiraAccess = await getInviteesWithoutJiraAccess(
				jiraCloudId,
				collaborators,
			);
			if (inviteesWithoutJiraAccess.length === 0) {
				fireExposure('COLLABORATORS_ALREADY_HAVE_ACCESS_TO_LINK');
				return [];
			}

			const inviteesWithConfluenceAccess = await getInviteesWithConfluenceAccess(
				confluenceCloudId,
				spaceKey,
				inviteesWithoutJiraAccess,
			);

			if (inviteesWithConfluenceAccess.length === 0) {
				fireExposure('COLLABORATORS_HAVE_NO_ACCESS_TO_SPACE');
				return [];
			}

			if (!(await canUserInvite(jiraCloudId))) {
				fireExposure('NOT_ELIGIBLE_FOR_INVITE');
				return [];
			}

			return inviteesWithConfluenceAccess;
		},
		[confluenceCloudId, fireExposure, userId],
	);
};
